export type HelpMedia = {
  type: 'image' | 'video'
  src: string
  title?: string
  caption?: string
}

export type HelpTopic = {
  id: string
  title: string
  summary: string
  steps?: string[]
  notes?: string[]
  media?: HelpMedia[]
}

export type HelpSection = {
  id: string
  title: string
  description: string
  topics: HelpTopic[]
}

export type HelpCategory = {
  id: string
  title: string
  description: string
  sections: HelpSection[]
}

export const helpContent: HelpCategory[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    description: 'Start here to learn how the admin portal is organized and prepare a store for its first customer order.',
    sections: [
      {
        id: 'get-started',
        title: 'Get Started',
        description: 'Learn the essentials before you create products, upload media, or review orders.',
        topics: [
          {
            id: 'welcome-to-admin-portal',
            title: 'Welcome to the admin portal',
            summary: 'Use the admin portal to manage the content and workflows behind the customer personalization experience.',
            steps: [
              'Use Orders to review customer requests and production status.',
              'Use Products to configure product profiles, 3D views, pricing, and decoration sections.',
              'Use Media to manage graphic categories, artwork, and fonts.',
              'Use Settings to control store configuration, branding, and themes.',
            ],
            notes: [
              'The options available to you can depend on your account permissions and the active store.',
            ],
            media: [],
          },
          {
            id: 'select-active-store',
            title: 'Select the active store',
            summary: 'Always confirm the active store before changing content or configuration.',
            steps: [
              'Check the store name in the top bar.',
              'Open the store selector when you need to work in another store.',
              'Confirm the new store name before making changes.',
            ],
            notes: [
              'Products, media, settings, and orders can be different for each store.',
            ],
            media: [],
          },
          {
            id: 'navigate-admin-portal',
            title: 'Find your way around',
            summary: 'The main menu gives you access to every administrative area for the selected store.',
            steps: [
              'Choose a main area from the left menu.',
              'Use page tabs to move between related tools.',
              'Use search and filters to find a specific record.',
              'Choose the Help link in a page header for guidance about that area.',
            ],
            notes: [
              'Contextual Help opens only the guides related to the page you are viewing.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'recommended-setup',
        title: 'Recommended Setup',
        description: 'Follow this order when you are preparing a new store or product experience.',
        topics: [
          {
            id: 'first-store-setup',
            title: 'Set up a store for the first time',
            summary: 'Complete the shared store resources before connecting them to individual products.',
            steps: [
              'Confirm the active store and review its Customizer Config settings.',
              'Add the graphic categories, graphics, and fonts customers can use.',
              'Create the required View Profiles for 3D products.',
              'Create the pricing models used by personalization.',
              'Create each product profile and add its decoration sections.',
              'Review the theme and test the complete customer experience.',
            ],
            notes: [
              'Creating shared media, View Profiles, and pricing first makes product setup faster and more consistent.',
            ],
            media: [],
          },
          {
            id: 'pre-publish-checklist',
            title: 'Check before publishing',
            summary: 'Test the complete customer flow before making a new setup available.',
            steps: [
              'Confirm the correct product image or 3D model is shown.',
              'Test every decoration section and its available graphics and fonts.',
              'Test pricing with realistic customer designs.',
              'Check the experience on the active theme.',
              'Place a test order and review its line-item results when possible.',
            ],
            notes: [
              'A saved configuration is not necessarily ready for customers until the full flow has been tested.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'using-help',
        title: 'Using Help',
        description: 'Use Guides for written instructions and Tutorials for short visual walkthroughs.',
        topics: [
          {
            id: 'find-help-guide',
            title: 'Find the right guide',
            summary: 'Browse by area or search for the task you need to complete.',
            steps: [
              'Open Help and stay on the Guides tab for written instructions.',
              'Choose a section from the contents menu to jump directly to it.',
              'Use search to filter guides by title.',
              'Open contextual Help from a page header when you only need guidance for that page.',
            ],
            media: [],
          },
          {
            id: 'watch-help-tutorial',
            title: 'Watch a tutorial',
            summary: 'Tutorial cards provide a short walkthrough for the task described by a guide.',
            steps: [
              'Choose a green-outlined tutorial card inside a related guide or open the Tutorials tab.',
              'Select Play tutorial to open the video.',
              'Use the video controls to play, pause, seek, or enter full screen.',
              'Close the video to continue reading the guide.',
            ],
            notes: [
              'Tutorial videos do not start automatically.',
            ],
            media: [],
          },
        ],
      },
    ],
  },
  {
    id: 'orders',
    title: 'Orders',
    description: 'Review customer orders, check production status, and reprocess work when something needs attention.',
    sections: [
      {
        id: 'order-review',
        title: 'Order Review',
        description: 'Use the order page to understand what the customer ordered and what still needs work.',
        topics: [
          {
            id: 'find-order',
            title: 'Find an order',
            summary: 'Search for an order when you need to review a customer request or production status.',
            steps: [
              'Open Orders from the menu.',
              'Search by order number, customer detail, or SKU when available.',
              'Open the order and review each line item.',
            ],
            notes: [
              'If the order is not listed, check the active store before searching again.',
            ],
            media: [],
          },
          {
            id: 'read-order-status',
            title: 'Read order status',
            summary: 'Use the order status to see where the order is in the workflow.',
            steps: [
              'Open the order.',
              'Review the status badges on the order and line items.',
              'Look for any failed or pending status before sending the order forward.',
            ],
            notes: [
              'A pending status usually means the system is still processing or waiting for a result.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'line-items',
        title: 'Line Items',
        description: 'Line items show each product in the order and the work needed for that item.',
        topics: [
          {
            id: 'review-line-item',
            title: 'Review a line item',
            summary: 'Check each line item before production work is completed.',
            steps: [
              'Open the order detail page.',
              'Review each product line item.',
              'Check the product, quantity, personalization data, and preview result.',
            ],
            notes: [
              'Review all line items. One order can contain multiple products with different statuses.',
            ],
            media: [],
          },
          {
            id: 'line-item-history',
            title: 'Line item history',
            summary: 'Use history when you need to see what happened to a line item over time.',
            steps: [
              'Open the line item.',
              'Review the available history or job details.',
              'Use the latest result when deciding the next action.',
            ],
            notes: [
              'History is helpful when a job was retried or a result changed after reprocessing.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'order-actions',
        title: 'Order Actions',
        description: 'Use order actions when an order needs to be checked again or moved forward.',
        topics: [
          {
            id: 'reprocess-order',
            title: 'Reprocess an order or line item',
            summary: 'Reprocess when a preview, image result, or production job needs to run again.',
            steps: [
              'Open the order or line item that needs attention.',
              'Choose the reprocess action.',
              'Wait for the status to update.',
              'Review the new result before taking the next step.',
            ],
            notes: [
              'Only reprocess when the current result is missing, failed, or clearly incorrect.',
            ],
            media: [],
          },
          {
            id: 'mark-order-done',
            title: 'Mark work as done',
            summary: 'Mark work as done only after the order or line item has been reviewed.',
            steps: [
              'Confirm the preview and production result look correct.',
              'Check that no line item still needs attention.',
              'Use the done action when the work is complete.',
            ],
            notes: [
              'Do not mark work as done if any line item still has a failed or pending result.',
            ],
            media: [],
          },
        ],
      },
    ],
  },
  {
    id: 'products',
    title: 'Products',
    description: 'Set up products so orders can use the right artwork areas, model view, and pricing.',
    sections: [
      {
        id: 'product-profiles',
        title: 'Product Profiles',
        description: 'A product profile is the setup for one sellable product. It controls the product view, pricing, and decoration sections.',
        topics: [
          {
            id: 'find-product-profile',
            title: 'Find a product profile',
            summary: 'Use the product list when you need to check or update an existing setup.',
            steps: [
              'Open Products from the menu.',
              'Use the search field to find the SKU or product name.',
              'Open the profile and review the selected view profile, pricing model, and sections.',
            ],
            notes: [
              'If a product is missing, confirm it has been synced from the store before creating a new profile.',
            ],
            media: [],
          },
          {
            id: 'what-product-profile-controls',
            title: 'What a product profile controls',
            summary: 'The product profile is the main setup page for personalization.',
            steps: [
              'Choose whether the profile uses a 2D or 3D setup.',
              'Select the view profile so the product preview looks correct.',
              'Select the pricing model for personalization.',
              'Add sections for every area where the customer can place artwork.',
              'Save after reviewing the preview and section list.',
            ],
            notes: [
              'If the product preview, pricing, or artwork areas are wrong, start by checking the product profile.',
            ],
            media: [],
          },
          {
            id: 'create-product-profile',
            title: 'Create a product profile and its sections',
            summary: 'Create the profile first, then continue in the same setup to define every area customers can personalize.',
            steps: [
              'Open Products and choose a new product profile.',
              'Select the product SKU first.',
              'Choose 2D or 3D for the profile type.',
              'Choose the view profile and pricing model.',
              'Continue to Sections in the same product profile and add the first decoration area.',
              'Give the section a clear name, then set its image, mask, dimensions, placement, and graphic category as needed.',
              'Review the profile preview and section list, then save the completed product setup.',
            ],
            notes: [
              'A product profile is only ready for customer design after it has at least one usable section.',
              'Keep profile and section names clear. Use the product name, size, and area when similar items exist.',
            ],
            media: [],
          },
          {
            id: '2d-product-profile',
            title: '2D product profile',
            summary: 'Use a 2D product profile when the customer design is placed on flat product images.',
            steps: [
              'Upload or select product images for the sections.',
              'Measure the image when the section size needs to match the real product.',
              'Use section images to show customers where their design will appear.',
              'Keep the 2D preview easy to recognize.',
            ],
            notes: [
              '2D profiles are usually easier to set up when a flat image gives enough detail for the customer.',
            ],
            media: [],
          },
          {
            id: '3d-product-profile',
            title: '3D product profile',
            summary: 'Use a 3D product profile when customers need to preview artwork on a model.',
            steps: [
              'Select the correct view profile for the product model.',
              'Add sections on the model where artwork can be placed.',
              'Check the preview from the customer point of view.',
              'Use screenshots or section previews when switching to 2D setup if needed.',
            ],
            notes: [
              '3D profiles work best when the model closely matches the real product shape.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'product-models',
        title: '3D Models',
        description: 'Prepare reusable 3D models, convert source files, and connect the finished model to a product profile.',
        topics: [
          {
            id: 'view-profile',
            title: 'Create a view profile',
            summary: 'A view profile stores the GLB or GLTF model and UV projection used by a 3D product profile.',
            steps: [
              'Open Products, select View Profiles, then choose Add View Profile.',
              'Enter a clear Profile Name and the product SKU.',
              'Upload the finished .glb or .gltf model and check its 3D preview.',
              'Choose Planar UVs for flat objects, Cylindrical for cups or bottles, Box for general shapes, or None to keep the UVs already stored in the GLB.',
              'Choose Add View Profile and wait for the model and thumbnail to finish saving.',
              'Open the product profile and select the new view profile in the View Profile field.',
            ],
            notes: [
              'If the source model is STEP or STP, convert and verify it in 3D Studio before creating the view profile.',
            ],
            media: [],
          },
          {
            id: 'step-to-glb',
            title: 'Convert STEP or STP to GLB',
            summary: 'Use the built-in converter to prepare a CAD model for View Profiles and the customer-facing 3D preview.',
            steps: [
              'Open Products and select the 3D Studio tab.',
              'Choose the Smoothness Quality for the STEP model and select the UV Projection that matches the product shape.',
              'Upload one .step or .stp file and wait for the model to load in the 3D viewer.',
              'Inspect every part in the Objects panel. Rename or remove parts when needed and confirm the model orientation and scale look correct.',
              'Select parts to adjust their position or material, then use UV Test Mode to check the selected projection.',
              'Choose Export, then As Single GLB to download the finished model.',
              'Create a View Profile and upload the exported GLB.',
            ],
            notes: [
              'A smoother STEP conversion creates more vertices. Use enough detail for curved surfaces without making the exported GLB unnecessarily heavy.',
              'Only one STEP or STP file can be loaded at a time. Multiple-file upload is supported for STL parts.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'pricing',
        title: 'Pricing',
        description: 'Create a reusable pricing model, define how personalization is charged, and verify the final customer price.',
        topics: [
          {
            id: 'choose-pricing-strategy',
            title: 'Choose a pricing strategy',
            summary: 'The pricing strategy decides which part of the customer design increases the personalization price.',
            steps: [
              'Use Fixed Price for Product when personalization always has one price.',
              'Use Price Per Section when each used decoration area should be charged.',
              'Use Price Per Element when each text, graphic, monogram, upload, or other customer-added element should be charged.',
              'Use Price Per Size when the price should change with the size of the customer design.',
            ],
            notes: [
              'Choose the strategy that matches what the store promises customers. The wrong strategy can produce a valid but unexpected price.',
            ],
            media: [],
          },
          {
            id: 'create-pricing-model',
            title: 'Create a pricing model',
            summary: 'Build the complete pricing model before assigning it to a product profile.',
            steps: [
              'Open Products, select Pricing, then choose Add Pricing Model.',
              'Enter a clear name, select the Pricing Strategy, and set the Base Price.',
              'For Section, Element, or Size pricing, add any Tiered Rules needed for the first items or tiers.',
              'Set the Subsequent Rule that applies after the configured tiered rules have been used.',
              'Set the Minimum Price and Maximum Price allowed for the complete personalization price.',
              'Save the pricing model and confirm it appears in the Pricing list.',
            ],
            notes: [
              'Fixed pricing does not use tiered or subsequent rules. Minimum and maximum prices still protect the final total.',
            ],
            media: [],
          },
          {
            id: 'pricing-rules',
            title: 'Tiered and subsequent pricing rules',
            summary: 'Use rules when the first item or tier should cost something different from everything that follows.',
            steps: [
              'Add a Tiered Rule for each early tier that needs special pricing.',
              'Choose Fixed Amount, Percentage Discount, or Amount Discount and enter its value.',
              'Add tiered rules in order so each item number receives the intended price.',
              'Choose a Subsequent Rule and value for all later items after the final tiered rule.',
              'Review the Base Price, rules, and minimum and maximum totals together before saving.',
            ],
            notes: [
              'The subsequent rule is the default for every later tier not covered by a tiered exception.',
            ],
            media: [],
          },
          {
            id: 'assign-test-pricing',
            title: 'Assign and test pricing',
            summary: 'Connect the pricing model to a product profile and verify realistic customer designs before publishing.',
            steps: [
              'Open the product profile and select the saved model in the Pricing Model field.',
              'Save the product profile, then open its customer preview.',
              'Test an empty design, one personalized item, and multiple sections or elements when the strategy supports them.',
              'For size pricing, test small and large designs near the expected boundaries.',
              'Confirm the final total respects the Minimum Price and Maximum Price.',
            ],
            notes: [
              'If the preview price is wrong, update the pricing model and retest the product before publishing it.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'sections',
        title: 'Sections',
        description: 'Sections are the design areas inside a product profile. Each section controls one place customers can add artwork.',
        topics: [
          {
            id: 'what-sections-control',
            title: 'What sections control',
            summary: 'A section tells the customer where they can design and what choices are available there.',
            steps: [
              'Use the section name to describe the area, such as Front, Back, Lid, or Wrap.',
              'Use the section image or preview to show where the artwork goes.',
              'Use a mask image when only part of the image should be available for design.',
              'Choose a specific graphic category when that section needs its own artwork choices.',
              'Review the section in the preview before saving.',
            ],
            notes: [
              'If customers are designing in the wrong place, check the section image, mask, and preview area first.',
            ],
            media: [],
          },
          {
            id: 'section-settings',
            title: 'Section settings',
            summary: 'Section settings decide how that design area behaves for the customer.',
            steps: [
              'Name: the label users see for the design area.',
              'Section image: the image or screenshot shown for that area.',
              'Mask image: the shape that limits where artwork can be placed.',
              'Specific graphic category: the artwork group shown only for this section.',
              'Status and visibility: whether the section is ready to use.',
              'Preview or measurement controls: tools used to confirm the size and placement.',
            ],
            notes: [
              'Only add the fields that matter for the product. A simple flat product may need fewer settings than a 3D product.',
            ],
            media: [],
          },
          {
            id: 'section-image',
            title: 'Section image',
            summary: 'The section image helps users understand where the design will appear.',
            steps: [
              'Upload a clean image for the section.',
              'Check that the image matches the selected product view.',
              'Re-measure the image if the size does not look right.',
            ],
            notes: [
              'Avoid cropped or blurry images. The image should make the decoration area easy to recognize.',
            ],
            media: [],
          },
        ],
      },
    ],
  },
  {
    id: 'media',
    title: 'Media',
    description: 'Manage reusable artwork groups and fonts used by product personalization.',
    sections: [
      {
        id: 'graphic-categories',
        title: 'Graphic Categories',
        description: 'Graphic categories group artwork so customers can browse the right choices.',
        topics: [
          {
            id: 'create-graphic-category',
            title: 'Create a graphic category',
            summary: 'Create a category when a group of graphics belongs together.',
            steps: [
              'Open Media.',
              'Open Graphic Categories.',
              'Add a category and use a short, customer-friendly name.',
              'Add a description and category thumbnail, then review the category options.',
              'Add the graphics that belong in that category.',
            ],
            notes: [
              'Use specific names when the category is only meant for one product area or product type.',
            ],
            media: [],
          },
          {
            id: 'graphic-category-options',
            title: 'Graphic category options',
            summary: 'Active, Show on Home, and Generic Category control whether a category is available and where it can be used.',
            steps: [
              'Turn on Active when the category is ready to use. Turn it off to keep the category saved but remove it from the normal active experience.',
              'Turn on Show on Home when the category should be featured on the customer-facing home or starting view.',
              'Turn on Generic Category when the artwork group is general rather than assigned as a specific category for one product section.',
              'Choose Update Category, then verify the category in the active store and customer experience.',
            ],
            notes: [
              'Show on Home and Active are separate options. A category intended for the home view should normally also be Active.',
              'Generic categories are not listed in the Specific Graphic Category field inside a product section. Leave Generic Category off when the category must be assigned there.',
            ],
            media: [],
          },
          {
            id: 'upload-graphic',
            title: 'Upload a graphic',
            summary: 'Add one or more graphics to a category, then manage individual or multiple images from the category grid.',
            steps: [
              'Open Media, choose Graphic Categories, then open the category that should contain the artwork.',
              'Drag one or more image files onto the page, or choose Add Image and select the files from your device.',
              'Wait for the upload progress to finish. Each file is added to the selected category when its upload completes.',
              'To delete one graphic, hover over its card, choose the trash icon, then confirm the deletion.',
              'To select multiple graphics, use the checkbox on each card, hold Shift to select a range, or drag a selection box across the category grid.',
              'Choose Delete Selected from the actions menu, review the selected count, then confirm the bulk deletion.',
              'If a bulk deletion was accidental, choose Undo from the success notification before it closes.',
            ],
            notes: [
              'Supported upload formats are JPG, JPEG, PNG, SVG, and WEBP. You can select and upload multiple files at the same time.',
              'Click an empty area of the category grid or choose Cancel from the actions menu to clear the current selection.',
            ],
            media: [],
          },
          {
            id: 'specific-graphic-category',
            title: 'Specific graphic category',
            summary: 'Use a specific graphic category when only one section should show those graphics.',
            steps: [
              'Edit the graphic category and confirm Generic Category is turned off.',
              'Open the product profile section.',
              'Choose the specific graphic category for that section.',
              'Save the profile and preview the customer choices.',
            ],
            notes: [
              'This keeps unrelated graphics out of the customer design flow.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'fonts',
        title: 'Fonts',
        description: 'Fonts control the text styles customers can use in the design experience.',
        topics: [
          {
            id: 'add-font',
            title: 'Add a font',
            summary: 'Add a font when customers need a new text style.',
            steps: [
              'Open Media.',
              'Open Fonts.',
              'Upload the font file and give it a clear name.',
              'Preview the font before saving.',
            ],
            notes: [
              'Use names that are easy to recognize. Avoid internal file names when they are unclear.',
            ],
            media: [],
          },
          {
            id: 'font-visibility',
            title: 'Font visibility',
            summary: 'Only keep fonts visible when they are ready for customer use.',
            steps: [
              'Review the font preview.',
              'Confirm it supports the characters customers need.',
              'Keep unused or test fonts inactive.',
            ],
            notes: [
              'Too many fonts can make the customer flow harder to use.',
            ],
            media: [],
          },
        ],
      },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Control store-level behavior, theme setup, and publishing choices.',
    sections: [
      {
        id: 'themes',
        title: 'Themes',
        description: 'Themes control how the customer personalization experience looks and behaves.',
        topics: [
          {
            id: 'edit-theme',
            title: 'Edit a theme',
            summary: 'Edit a theme when the customer experience needs a visual or content update.',
            steps: [
              'Open Settings.',
              'Choose the theme you want to update.',
              'Open the theme editor.',
              'Make the changes and save the theme.',
            ],
            notes: [
              'Review the theme before publishing changes for customers.',
            ],
            media: [],
          },
          {
            id: 'publish-theme',
            title: 'Publish a theme',
            summary: 'Publish only after the theme has been reviewed with the right product setup.',
            steps: [
              'Confirm the theme looks correct.',
              'Check the main product flow.',
              'Publish when the experience is ready for customers.',
            ],
            notes: [
              'Publishing can affect what customers see, so review changes before making them live.',
            ],
            media: [],
          },
        ],
      },
      {
        id: 'store-settings',
        title: 'Store Settings',
        description: 'Configure production tools, customer customization rules, and branding for the active store.',
        topics: [
          {
            id: 'active-store',
            title: 'Active store',
            summary: 'Settings are saved for the store that is currently selected in the top bar.',
            steps: [
              'Check the active store name in the top bar before opening Settings.',
              'Open the store menu and select another store when needed.',
              'Return to Settings and confirm the selected store before making changes.',
            ],
            notes: [
              'Store settings do not automatically carry over to other stores.',
            ],
            media: [],
          },
          {
            id: 'machine-profile-version',
            title: 'Machine and profile version',
            summary: 'Choose whether the store uses the Neatolabs machine workflow and version 2 product profiles.',
            steps: [
              'Open Settings and select the Customizer Config tab.',
              'Turn on Neatolabs Machine when the store uses the Neatolabs production workflow.',
              'Turn on Profile Version V2 when products use the version 2 profile editor.',
              'Confirm the options remain selected after the update completes.',
            ],
            notes: [
              'These options save when toggled and can change which product and machine tools are available.',
            ],
            media: [],
          },
          {
            id: 'customization-element-size',
            title: 'Customization element size',
            summary: 'Set the minimum size of customer-added elements and the minimum resolution for uploaded images.',
            steps: [
              'Open Settings, select Customizer Config, then open Customization Element Size.',
              'Enter minimum sizes for Text, Graphic, AI Image, Monogram, and Upload Graphic elements.',
              'Enter the minimum uploaded image width and height in pixels.',
              'Choose Save to apply the values to the active store.',
            ],
            notes: [
              'All size and resolution values must be positive whole numbers.',
            ],
            media: [],
          },
          {
            id: 'profanity-rules',
            title: 'Profanity rules',
            summary: 'Control how customer text is accepted, sent for manual review, or rejected based on its score.',
            steps: [
              'Open Settings, select Customizer Config, then open Profanity Rules.',
              'Enable the result statuses the store needs: Accepted, Requires Manual Check, or Rejected.',
              'Adjust the percentage boundary for each active status.',
              'Use Test Text to check a phrase against the current policy.',
              'Choose Save when the result ranges behave as expected.',
            ],
            notes: [
              'Active score ranges always cover 0% to 100%. Test important boundary cases before saving.',
            ],
            media: [],
          },
          {
            id: 'store-branding',
            title: 'Store branding',
            summary: 'Set the company logo and brand color used for the active store.',
            steps: [
              'Open Settings and select the Customizer Config tab.',
              'Upload a valid image in Company Logo, or remove the current logo when it is no longer needed.',
              'Choose a Brand Color or enter an exact six-digit hex value such as #000000.',
              'Wait for the success message before leaving the page or switching stores.',
            ],
            notes: [
              'Logo and brand color changes save automatically. Confirm the active store before changing either value.',
            ],
            media: [],
          },
        ],
      },
    ],
  },
]
